/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import Textarea from "../../../components/ReactHookFormFields/General/Textarea";
import { strings } from "../../../common/Strings/Strings";
import Button from "../../../components/Button";
import logger from "../../../util/logger";
import AlertBox, { AlertType } from "../../../components/AlertBox";
import SupportApi from "../../../api/SupportApi";

type SupportForm = {
  problem: string;
};

export const SlackPage: React.FC = () => {
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<SupportForm>({ mode: "onSubmit" });
  const { user } = useUser();
  const navigate = useNavigate();

  const onSubmit = async ({ problem }: SupportForm) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    try {
      if (user?.userId) {
        await SupportApi.sendSupportMessage({ message: problem });
        setSuccess(true);
        navigate("/help", {
          state: {
            message: strings.slackSuccess,
          },
        });
      }
    } catch (e) {
      logger.error(error);
      setError(strings.formatString(strings.supportNotAvailable, <Link to="mailto:support@globalvet.com">support@globalvet.com</Link>));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="main-signed-in">
      <section>
        <div className="flex flex-col items-center justify-center space-y-6 mx-auto px-4 lg:px-0 py-10 md:py-14">
          <div className="lg:tw-card w-full space-y-6 lg:w-auto lg:p-8 w-md">
            <div className="flex justify-between space-x-4">
              <h1 className="text-xl font-semibold leading-tight text-zinc-800 dark:text-white lg:text-2xl">
                {strings.contactSupport}
              </h1>
            </div>
            {user?.userId ? (
              <form className="space-y-6 w-full md:w-96" onSubmit={handleSubmit(onSubmit)}>
                <Textarea
                  error={errors.problem}
                  label={strings.slackSupportProblemLabel}
                  name="problem"
                  minRows={4}
                  maxLength={1000}
                  showCharacterCount
                  register={register}
                  required
                />
                <AlertBox hidden={!success} message={strings.slackSuccess} type={AlertType.SUCCESS} />
                <AlertBox message={error} />
                <Button disabled={loading} loading={loading} type="submit">
                  {strings.emailSend}
                </Button>
              </form>
            ) : (
              <div className="space-y-6">
                <p>{strings.signInToContactUs}</p>
                <div>
                  <Link to="/login">
                    <Button>{strings.signIn}</Button>
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex justify-start">
                    <span className="text-left font-medium text-sm text-gray-500 dark:text-gray-400">
                      {strings.noAccountYet}
                    </span>
                  </div>
                  <div className="flex justify-end">
                    <Link className="text-right text-sm" to="/registration">
                      {strings.signUpHere}
                    </Link>
                  </div>
                </div>
              </div>
            )}
            <div>
              <Link className="text-sm" to="/help">
                {strings.goBackTohelpCenter}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SlackPage;
